// Project-level Problems page.
//
// Runs validateProject() against every cabinet + every adjacent pair
// and presents a filterable table. Each row links back to the cabinet
// in the design view by selecting it (`?selectedId=N` query param the
// editor reads on mount). Click on the part column jumps straight into
// the PartEditor with the offending part pre-selected.

import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useCabinetsIn, useCollection, useProject } from "@/lib/ddb-hooks";
import { indexCatalogs } from "@/lib/cabinets/bom";
import { validateProject, type Issue, type IssueSeverity } from "@/lib/cabinets/validation";
import type { EdgeBand, Hardware, Material } from "@/lib/types";

export function ProblemsPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const numId = id ? Number(id) : null;

  const { data: project, isLoading: pLoading } = useProject(numId);
  const { data: cabinets } = useCabinetsIn(numId);
  const { data: materials } = useCollection<Material>("materials");
  const { data: edgeBands } = useCollection<EdgeBand>("edge_bands");
  const { data: hardware } = useCollection<Hardware>("hardware");

  const issues = useMemo<Issue[]>(() => {
    if (!cabinets || !materials || !edgeBands || !hardware) return [];
    const catalog = indexCatalogs(materials, edgeBands, hardware);
    return validateProject(cabinets, catalog);
  }, [cabinets, materials, edgeBands, hardware]);

  const [filterSeverity, setFilterSeverity] = useState<"all" | IssueSeverity>("all");
  const [filterCode, setFilterCode] = useState<string>("all");

  const codes = useMemo(() => {
    const s = new Set<string>();
    for (const i of issues) s.add(i.code);
    return [...s].sort();
  }, [issues]);

  const filtered = useMemo(() => {
    return issues.filter((i) =>
      (filterSeverity === "all" || i.severity === filterSeverity)
      && (filterCode === "all" || i.code === filterCode)
    );
  }, [issues, filterSeverity, filterCode]);

  if (!id || Number.isNaN(Number(id))) return <div className="empty-state">Invalid project id.</div>;
  if (pLoading) return <div className="empty-state">Loading project…</div>;
  if (!project) return <div className="empty-state">Project not found.</div>;

  const errorCount = issues.filter((i) => i.severity === "error").length;
  const warnCount  = issues.length - errorCount;

  return (
    <div style={{ padding: "16px 24px", display: "grid", gridTemplateRows: "auto auto 1fr", height: "100%", gap: 12 }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Problems</h2>
        <span className="muted">·</span>
        <span className="muted">{project.name}</span>
        <span className="muted">·{" "}
          <span style={{ color: errorCount > 0 ? "var(--danger)" : "var(--ok)" }}>
            {errorCount} error{errorCount === 1 ? "" : "s"}
          </span>
          {" / "}
          <span style={{ color: warnCount > 0 ? "var(--accent, #ffaa55)" : "var(--ok)" }}>
            {warnCount} warning{warnCount === 1 ? "" : "s"}
          </span>
        </span>
        <span style={{ flex: 1 }} />
        <button className="ghost" onClick={() => nav(`/projects/${id}/design`)}>← Back to design</button>
      </header>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <label style={{ fontSize: 12 }}>
          <span className="muted" style={{ marginRight: 4 }}>Severity</span>
          <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value as "all" | IssueSeverity)}>
            <option value="all">All</option>
            <option value="error">Errors</option>
            <option value="warning">Warnings</option>
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          <span className="muted" style={{ marginRight: 4 }}>Code</span>
          <select value={filterCode} onChange={(e) => setFilterCode(e.target.value)}>
            <option value="all">All</option>
            {codes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <span className="muted" style={{ fontSize: 12, marginLeft: "auto" }}>
          {filtered.length} of {issues.length} shown
        </span>
      </div>

      <div style={{ overflowY: "auto", border: "1px solid var(--line)", borderRadius: 4 }}>
        {filtered.length === 0 ? (
          <div className="muted" style={{ padding: 24, textAlign: "center", fontSize: 13 }}>
            {issues.length === 0
              ? "Project is clean — no issues found."
              : "No issues match the current filters."}
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead style={{ position: "sticky", top: 0, background: "var(--bg)" }}>
              <tr style={{ color: "var(--fg-dim)" }}>
                <th style={th}>Severity</th>
                <th style={th}>Code</th>
                <th style={th}>Cabinet</th>
                <th style={th}>Part</th>
                <th style={{ ...th, width: "100%" }}>Message</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((iss, i) => (
                <tr
                  key={i}
                  style={{
                    cursor: iss.cabinetId != null ? "pointer" : "default",
                    borderTop: "1px solid var(--line)",
                  }}
                  onClick={() => {
                    if (iss.cabinetId == null) return;
                    nav(`/projects/${id}/design?selectedId=${iss.cabinetId}`);
                  }}
                  title={iss.cabinetId != null ? "Open in design view" : ""}
                >
                  <td style={{ ...td, color: iss.severity === "error" ? "var(--danger)" : "var(--accent, #ffaa55)" }}>
                    {iss.severity === "error" ? "⚠ error" : "⚠ warning"}
                  </td>
                  <td style={{ ...td, fontFamily: "monospace" }}>{iss.code}</td>
                  <td style={td}>
                    {iss.cabinetId != null ? `#${iss.cabinetId}` : "—"}
                    {iss.otherCabinetId != null && <> ↔ #{iss.otherCabinetId}</>}
                  </td>
                  <td style={td}>{iss.partId ?? "—"}</td>
                  <td style={td}>{iss.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 12px",
  fontWeight: 500,
  borderBottom: "1px solid var(--line)",
};
const td: React.CSSProperties = {
  padding: "6px 12px",
  verticalAlign: "top",
};
